/**
 * Pure aggregation logic for the Student card «Needs attention» panel (Area 2).
 *
 * `buildStudentAlerts` is a side-effect-free projection over six already-computed
 * source states (overdue contributions, outstanding invoices, orphan invoices,
 * orphan payments, unsettled accounts payable, and missing/lost PandaDoc). It
 * decides which alerts are shown, what count each carries, where each one
 * deep-links to, and which categories are un-evaluable (their source failed).
 *
 * This module avoids importing React/Apollo/generated types: the
 * `StudentTabId` / `PaymentsStatusFilter` unions are declared here as the
 * canonical pure-layer source (mirroring `tabIds.ts` byte-for-byte for the tab
 * values), so the deep-link util and components import them from here.
 */

/** The five Student card tabs. Mirrors `StudentDetail/tabIds.ts`. */
export type StudentTabId =
  | 'overview'
  | 'agreements'
  | 'payments'
  | 'invoices'
  | 'pandadoc';

/** The Payments tab status filter chips. */
export type PaymentsStatusFilter = 'all' | 'unpaid' | 'paid';

/** The six fixed alert categories surfaced by the panel (Req 5.1). */
export type StudentAlertId =
  | 'overdueContributions'
  | 'outstandingInvoices'
  | 'orphanInvoices'
  | 'orphanPayments'
  | 'unsettledPayables'
  | 'pandadocMissingOrLost';

/** An in-tab scroll anchor a target may point at. */
export type StudentAlertBlock =
  | 'orphanInvoices'
  | 'accountsPayable'
  | 'needsLinking';

/** Where selecting an alert navigates to (and the filter it applies, if any). */
export interface IStudentAlertTarget {
  tab: StudentTabId;
  /** In-tab scroll anchor brought into the viewport on navigation. */
  block?: StudentAlertBlock;
  /** Payments-tab status filter applied + encoded where the target supports it. */
  paymentsStatus?: PaymentsStatusFilter;
}

/**
 * The already-computed state of one alert category supplied by its source hook:
 * the affected-item count, whether that source is still loading, and whether it
 * failed (un-evaluable — Req 5.8).
 */
export interface IStudentAlertSource {
  count: number;
  loading: boolean;
  failed?: boolean;
}

/** A single displayable alert: its category, affected-item count, and target. */
export interface IStudentAlert {
  id: StudentAlertId;
  count: number;
  target: IStudentAlertTarget;
}

/** The derived alerts state consumed by the panel. */
export interface IStudentAlertsState {
  /** Alerts already filtered to `count > 0`, in canonical category order. */
  alerts: IStudentAlert[];
  /** `true` iff every category finished loading (gates the «no issues» state). */
  allLoaded: boolean;
  /** `true` iff at least one category is still loading (drives the loading state). */
  anyLoading: boolean;
  /** Categories whose source failed — shown distinctly from «no issues» (Req 5.8). */
  unevaluable: StudentAlertId[];
}

/**
 * Fixed category → navigation-target mapping (Req 6.1–6.6). Constant: an alert
 * always deep-links to the same tab/block/filter regardless of counts.
 */
export const STUDENT_ALERT_TARGETS: Readonly<
  Record<StudentAlertId, IStudentAlertTarget>
> = {
  overdueContributions: { tab: 'payments', paymentsStatus: 'unpaid' },
  outstandingInvoices: { tab: 'invoices' },
  orphanInvoices: { tab: 'overview', block: 'orphanInvoices' },
  orphanPayments: { tab: 'payments', block: 'needsLinking' },
  unsettledPayables: { tab: 'overview', block: 'accountsPayable' },
  pandadocMissingOrLost: { tab: 'pandadoc' },
};

/** Canonical display order for the alert categories. */
export const STUDENT_ALERT_ORDER: readonly StudentAlertId[] = [
  'overdueContributions',
  'outstandingInvoices',
  'orphanInvoices',
  'orphanPayments',
  'unsettledPayables',
  'pandadocMissingOrLost',
];

/**
 * Build the «Needs attention» alerts state from the six source states.
 *
 * - `alerts` contains one entry per category whose `count > 0` AND that did not
 *   fail, in canonical order, each carrying its true count and fixed target
 *   (Req 5.2, 5.4, Property 5).
 * - A still-loading category sets `anyLoading` and clears `allLoaded` (Req 5.7).
 * - A failed category is listed in `unevaluable` and never counted as an alert
 *   or as a reason to show «no issues» (Req 5.8, Property 6).
 */
export const buildStudentAlerts = (
  input: Record<StudentAlertId, IStudentAlertSource>,
): IStudentAlertsState => {
  const alerts: IStudentAlert[] = [];
  const unevaluable: StudentAlertId[] = [];
  let allLoaded = true;
  let anyLoading = false;

  STUDENT_ALERT_ORDER.forEach((id) => {
    const { count, loading, failed } = input[id];

    if (loading) {
      anyLoading = true;
      allLoaded = false;
    }

    if (failed) {
      unevaluable.push(id);
      return;
    }

    if (count > 0) {
      alerts.push({ id, count, target: STUDENT_ALERT_TARGETS[id] });
    }
  });

  return { alerts, allLoaded, anyLoading, unevaluable };
};

/**
 * Whether the localized «no issues» state may be shown: only once every category
 * has finished loading, none produced a positive count, and none is unevaluable
 * (Req 5.3, Property 6).
 */
export const canShowNoIssues = (state: IStudentAlertsState): boolean =>
  state.allLoaded &&
  state.alerts.length === 0 &&
  state.unevaluable.length === 0;
